import type { Auth0Error } from 'auth0-js';

import { logError } from '../../../utils/datadog';
import type { TrackLinkFn } from '../../analytics/types';
import type {
  Auth0ChangePasswordErrorName,
  Auth0ErrorCode,
  Auth0PasswordLessErrorCode,
  FriendlyChangePasswordError,
  FriendlyError,
  FriendlyPasswordLessClientError,
  FriendlyPasswordLessServerError,
  FriendlyRecaptchaError,
  PasswordLessClientErrorName,
  PasswordLessServerErrorDesc,
  RecaptchaNonResetErrorName,
} from '../types';

export const genericError: FriendlyError =
  'Something went wrong. Please try again later.';

export const mappings: Record<Auth0ErrorCode, FriendlyError> = {
  invalid_password: 'Password is invalid. Please choose a different password.',
  too_many_attempts:
    'Something’s not right. We’ve sent instructions on how to reset your password to your email.',
  unauthorized:
    'Are you sure that’s right? We don’t recognise that combination.',
  invalid_user_password:
    'Are you sure that’s right? We don’t recognise that combination.',
  user_exists:
    'You can’t register with an email already in use. Please sign in or use a different email.',
  duplicate_email_address:
    'Email address is registered to multiple accounts. Please contact customer service.',
  rate_limited:
    'We have detected too many requests from your IP address. Please try again after ten minutes or contact customer service.',
  signup_on_sso_domain:
    'This email is eligible for single sign on. Please use "Sign in with your identity provider" instead.',
  password_change_required:
    'Your password has expired and a new one is required to access your account. Please change your password by clicking the ‘Forgot password?’ link above.',
  pre_registration_error: genericError, // for debug purpose to keep generic error msg with different code
  migration_timeout:
    'Unfortunately, there is a system issue with your account at this time. We are working on it now, please try again later.',
  migration_force_password_reset:
    'Your password has expired and a new one is required to access your account. Please change your password by clicking the ‘Forgot password?’ link above.',

  /**
   * N.B The login_not_allowed does not need to be translated as it only
   * concerns users who are migrating from Asia but not being from
   * the Philippines when launching for the First Phase of the
   * Data Migration for APAC.
   */
  login_not_allowed:
    'Sorry, you are temporarily unable to login to JobStreet Philippines with this account.',
  recaptcha_error: 'Please complete the recaptcha.',
};

export const passwordLessMappings: Record<
  Auth0PasswordLessErrorCode,
  FriendlyError
> = {
  too_many_attempts:
    'Something’s not right. We’ve sent instructions on how to proceed.',
  invalid_user_password: 'Invalid code. Try again or click resend below.',
  rate_limited:
    'We have detected too many requests from your IP address. Please try again after ten minutes or contact customer service.',
  pre_registration_error: genericError, // for debug purpose to keep generic error msg with different code
  recaptcha_error: 'Please complete the recaptcha.',
};

export const invalidPasswordErrors: Record<
  Auth0ChangePasswordErrorName,
  FriendlyChangePasswordError
> = {
  PasswordDictionaryError:
    'Password is too common. Please choose a different password.',
  PasswordStrengthError:
    'Password is too weak. Please choose a different password.',
  PasswordNoUserInfoError:
    'Password cannot contain user information. Please choose a different password.',
  PasswordHistoryError:
    'Password has previously been used. Please choose a different password.',
};

export const recaptchaNonResetErrors: Record<
  RecaptchaNonResetErrorName,
  FriendlyRecaptchaError
> = {
  recaptcha_required: 'Please complete the recaptcha.',
  error_rendering_recaptcha:
    'Error rendering recaptcha. Please refresh the page or contact customer service.',
};

export const passwordLessClientErrors: Record<
  PasswordLessClientErrorName,
  FriendlyPasswordLessClientError
> = {
  code_required: 'Enter your 6 digit code',
  code_length_required: 'The code should be 6 digits',
};

export const passwordLessServerErrors: Record<
  PasswordLessServerErrorDesc,
  FriendlyPasswordLessServerError
> = {
  'Wrong email or verification code.':
    'Invalid code. Try again or click resend below.',
  'The verification code has expired. Please try to login again.':
    'The code has expired.',
  "You've reached the maximum number of attempts. Please try to login again.":
    'You’ve reached the maximum number of attempts. Please click resend below.',
};

export const mapError = (
  error: Auth0Error,
  trackLink: TrackLinkFn,
  errorPath: string = 'UnknownPath',
): FriendlyError => {
  const code = error?.code as Auth0ErrorCode;
  let errorName;

  switch (code) {
    case 'too_many_attempts':
      trackLink({
        eventName: 'account_locked_warning_viewed',
      });
      return mappings.too_many_attempts;
    case 'invalid_password':
      errorName = error?.name as Auth0ChangePasswordErrorName;
      return invalidPasswordErrors[errorName] || mappings.invalid_password;
    case 'recaptcha_error':
      errorName = error?.name as RecaptchaNonResetErrorName;
      return recaptchaNonResetErrors[errorName] || genericError;
    default:
      const mappedError = mappings[code];

      if (!mappedError) {
        logError(error, `Unexpected Auth0 error. errorPath:${errorPath}`);
      }

      return mappedError || genericError;
  }
};

// Separating the password less map error function.
// This is to clean delete original mapError function when full passwordless will be released
export const passwordLessMapError = (
  error: Auth0Error,
  trackLink: TrackLinkFn,
  errorPath: string = 'UnknownPath',
): FriendlyError => {
  const code = error?.code as Auth0PasswordLessErrorCode;

  switch (code) {
    case 'too_many_attempts':
      trackLink({
        eventName: 'account_locked_warning_viewed',
      });
      return passwordLessMappings.too_many_attempts;
    case 'invalid_user_password':
      const errorDesc = error?.description as PasswordLessServerErrorDesc;
      return (
        passwordLessServerErrors[errorDesc] ||
        passwordLessMappings.invalid_user_password
      );
    case 'recaptcha_error':
      const errorName = error?.name as RecaptchaNonResetErrorName;
      return recaptchaNonResetErrors[errorName] || genericError;
    default:
      const mappedError = passwordLessMappings[code];

      if (!mappedError) {
        // TODO: Rearrange args
        logError(
          `Unexpected passwordless Auth0 error. errorPath:${errorPath}`,
          error,
        );
      }

      return mappedError || genericError;
  }
};
